/**
 * Engagement message template enums.
 *
 * These mirror the backend `TemplateType` / `TemplateLevelEnum` GraphQL enums
 * exactly — a value that does not exist server-side is rejected at validation
 * time, so keep both lists in sync with `api/schema.py`.
 */

export const TEMPLATE_TYPES = Object.freeze({
  SURVEY: "SURVEY",
  REVIEW: "REVIEW",
  ENGAGEMENT: "ENGAGEMENT",
  EMAIL: "EMAIL",
  TELEPROMPTER: "TELEPROMPTER",
});

export const TEMPLATE_TYPE_LABELS = Object.freeze({
  [TEMPLATE_TYPES.SURVEY]: "Survey",
  [TEMPLATE_TYPES.REVIEW]: "Review",
  [TEMPLATE_TYPES.ENGAGEMENT]: "Engagement",
  [TEMPLATE_TYPES.EMAIL]: "Email",
  [TEMPLATE_TYPES.TELEPROMPTER]: "Teleprompter",
});

export const TEMPLATE_TYPE_OPTIONS = Object.values(TEMPLATE_TYPES).map(value => ({
  value,
  label: TEMPLATE_TYPE_LABELS[value],
}));

/**
 * Scope a template belongs to. Single-valued: a template is owned by exactly
 * one level, and the id fields the level needs (locationId, departmentId, …)
 * are only meaningful for that level.
 */
export const TEMPLATE_LEVELS = Object.freeze({
  COMPANY: "COMPANY",
  LOCATION: "LOCATION",
  DEPARTMENT: "DEPARTMENT",
  OPPORTUNITY_TYPE: "OPPORTUNITY_TYPE",
  USER: "USER",
});

export const TEMPLATE_LEVEL_LABELS = Object.freeze({
  [TEMPLATE_LEVELS.COMPANY]: "Company",
  [TEMPLATE_LEVELS.LOCATION]: "Location",
  [TEMPLATE_LEVELS.DEPARTMENT]: "Department",
  [TEMPLATE_LEVELS.OPPORTUNITY_TYPE]: "Opportunity Type",
  [TEMPLATE_LEVELS.USER]: "Only me",
});

/**
 * Levels offered in the template form. OPPORTUNITY_TYPE is a valid backend
 * level but has no picker yet, so it is intentionally not selectable.
 */
export const TEMPLATE_LEVEL_OPTIONS = [
  TEMPLATE_LEVELS.USER,
  TEMPLATE_LEVELS.LOCATION,
  TEMPLATE_LEVELS.DEPARTMENT,
  TEMPLATE_LEVELS.COMPANY,
].map(value => ({ value, label: TEMPLATE_LEVEL_LABELS[value] }));

/**
 * Drawer widths for the template surfaces, in px. Applied on desktop only —
 * callers fall back to a percentage on smaller screens via `useDevice()`.
 *
 * The picker no longer carries scope tabs or a sort control, so it is sized to
 * the message preview instead: 800px keeps a two-line clamp at roughly the
 * same wrap point as the composer it is inserting into.
 */
export const TEMPLATE_FORM_DRAWER_WIDTH = 600;
export const TEMPLATE_PICKER_DRAWER_WIDTH = 800;

/**
 * The picker's "All" category — every template, whatever its type, and what the
 * picker opens on. A sentinel rather than `null` because `null` already means
 * "no category is active", which is the state a running search puts the chips
 * in; the two would otherwise be indistinguishable and All would light up while
 * the list is showing search results instead.
 */
export const TEMPLATE_ALL_TYPES = "ALL_TYPES";

/** Lines of message shown before "See more" takes over, in the picker and the table. */
export const TEMPLATE_MESSAGE_PREVIEW_LINES = 2;

/**
 * Which template contract the API speaks.
 *
 * The level-scoped, paginated contract is live: `level` / `isGlobal` select the
 * tier, paging is server-side, and the query returns a
 * PaginatedEngagementMessageTemplate envelope. The pre-deploy shape — scope
 * riding on `templateType` as a pseudo-value, a bare list, a `userId`
 * argument — is kept behind an opt-out for any environment whose API has not
 * caught up yet.
 *
 * Set GATSBY_TEMPLATES_V2=false to fall back. Read lazily so a build-time env
 * var and a test can both set it.
 */
export const isTemplatesV2 = () => process.env.GATSBY_TEMPLATES_V2 !== "false";

/**
 * Pseudo-values the pre-deploy schema accepts on `templateType` to filter by
 * scope. Same keys as TEMPLATE_SCOPES.
 */
export const LEGACY_SCOPE_TEMPLATE_TYPES = Object.freeze({
  all: "ALL",
  own: "OWN",
  company: "COMPANY",
  location: "LOCATION",
  global: "GLOBAL",
});

/**
 * Scope choices the UI offers when listing templates. These are view-level
 * keys, not server values — `buildTemplateScopeVariables` turns them into the
 * query's `level` / `isGlobal` / id arguments.
 */
export const TEMPLATE_SCOPES = Object.freeze({
  ALL: "all",
  MINE: "own",
  COMPANY: "company",
  LOCATION: "location",
  DEPARTMENT: "department",
  OPPORTUNITY_TYPE: "opportunityType",
  GLOBAL: "global",
});

/**
 * Server-side sort tokens accepted by `engagementMessageTemplates.orderBy`.
 * A sortable table column uses the token as its `key`, so antd hands the token
 * straight back on sort.
 */
export const TEMPLATE_SORT_FIELDS = Object.freeze({
  ID: "id",
  CREATED_ON: "created_on",
  UPDATED_ON: "updated_on",
  TITLE: "title",
  MESSAGE: "message",
  TEMPLATE_TYPE: "template_type",
  USER_NAME: "user_name",
});

/**
 * How the list is ordered until the user sorts it themselves.
 *
 * Applied to the query only — no column is marked as sorted, so the table opens
 * with a clean header and the arrows still start from their neutral state. The
 * ordering is not a filter the user chose, so showing it as one would invite
 * them to "clear" a sort they never set.
 */
export const DEFAULT_TEMPLATE_SORT = Object.freeze({
  orderBy: TEMPLATE_SORT_FIELDS.ID,
  orderDirection: "desc",
});

/** Sort choices offered where there are no column headers to click. */
export const TEMPLATE_SORT_OPTIONS = [
  { value: `${TEMPLATE_SORT_FIELDS.UPDATED_ON}:desc`, label: "Recently updated" },
  { value: `${TEMPLATE_SORT_FIELDS.CREATED_ON}:desc`, label: "Newest first" },
  { value: `${TEMPLATE_SORT_FIELDS.TITLE}:asc`, label: "Name (A–Z)" },
  { value: `${TEMPLATE_SORT_FIELDS.TITLE}:desc`, label: "Name (Z–A)" },
  { value: `${TEMPLATE_SORT_FIELDS.TEMPLATE_TYPE}:asc`, label: "Type" },
  { value: `${TEMPLATE_SORT_FIELDS.USER_NAME}:asc`, label: "Author" },
];

/**
 * Page size for the surfaces that fetch a template list once and then filter
 * and page it client-side (the composer picker, the email pickers). The query
 * is paginated server-side, so leaving pageSize off would silently truncate
 * those lists to the backend default.
 */
export const TEMPLATE_LIST_PAGE_SIZE = 200;

/**
 * Tag colour per template type, in the table's Type column and the picker's
 * category chips. antd preset names rather than hex so the tags pick up the
 * theme's pastel background/border pair.
 */
export const TEMPLATE_TYPE_TAG_COLORS = Object.freeze({
  [TEMPLATE_TYPES.ENGAGEMENT]: "blue",
  [TEMPLATE_TYPES.REVIEW]: "gold",
  [TEMPLATE_TYPES.SURVEY]: "green",
  [TEMPLATE_TYPES.EMAIL]: "cyan",
  [TEMPLATE_TYPES.TELEPROMPTER]: "purple",
});

/** Fallback for a type this build does not know — a user-created one. */
export const TEMPLATE_CUSTOM_TYPE_TAG_COLOR = "green";

export const getTemplateTypeTagColor = templateType =>
  TEMPLATE_TYPE_TAG_COLORS[templateType] || TEMPLATE_CUSTOM_TYPE_TAG_COLOR;

/**
 * Scope wording for the table's Scope column. Deliberately shorter than
 * TEMPLATE_LEVEL_LABELS, which labels the form's radio group: the column says
 * what a row *is* ("My template"), the form asks who to share it with
 * ("Only me").
 */
export const TEMPLATE_SCOPE_COLUMN_LABELS = Object.freeze({
  [TEMPLATE_LEVELS.USER]: "My template",
  [TEMPLATE_LEVELS.LOCATION]: "Location",
  [TEMPLATE_LEVELS.DEPARTMENT]: "Department",
  [TEMPLATE_LEVELS.OPPORTUNITY_TYPE]: "Opportunity Type",
  [TEMPLATE_LEVELS.COMPANY]: "Company",
});

/**
 * Channels a template can be sent on. A template is text by default and must
 * keep at least one channel.
 */
export const TEMPLATE_CHANNELS = Object.freeze({
  TEXT: "TEXT",
  EMAIL: "EMAIL",
});

export const TEMPLATE_CHANNEL_LABELS = Object.freeze({
  [TEMPLATE_CHANNELS.TEXT]: "Text",
  [TEMPLATE_CHANNELS.EMAIL]: "Email",
});

export const TEMPLATE_CHANNEL_OPTIONS = Object.values(TEMPLATE_CHANNELS).map(value => ({
  value,
  label: TEMPLATE_CHANNEL_LABELS[value],
}));

/**
 * "Every department at this location, including ones added later" — carried as
 * a value inside the department picker so a row needs one control, not two.
 * `normaliseDepartmentScopes` turns it back into the `allDepartments` flag the
 * mutation takes.
 */
export const ALL_DEPARTMENTS_VALUE = "all-departments";

/** Text is what a template is unless the author says otherwise. */
export const TEMPLATE_DEFAULT_CHANNELS = [TEMPLATE_CHANNELS.TEXT];

/** `null` is "All" — the Templates tab's channel filter is not narrowed. */
export const TEMPLATE_CHANNEL_FILTER_OPTIONS = [
  { value: null, label: "All" },
  ...TEMPLATE_CHANNEL_OPTIONS,
];

/**
 * Deploy gates for the two redesigned controls the API cannot store yet.
 *
 * `saveEngagementMessageTemplate` has no channel argument, and `templateType`
 * is a `TemplateTypeEnum` — a server enum, so a user-created type is rejected
 * at validation time and there is no mutation to create or delete one. Both
 * controls are built to the design and rendered only once their API lands;
 * shipping them on would silently drop what the author picked on save.
 *
 * Turning these on requires, respectively:
 *  - `channels: [String]` on the template type and the save mutation, plus a
 *    `channels` filter argument on `engagementMessageTemplates`;
 *  - a template-types collection (query + save/delete mutations) and
 *    `templateType` widened from an enum to a string.
 */
export const TEMPLATE_CHANNELS_ENABLED = false;
export const TEMPLATE_CUSTOM_TYPES_ENABLED = false;
